import React, { useState, useEffect, useCallback } from "react"
import { Box, Button, Flex, Text, Table, Thead, Tbody, Tr, Th, Td, Badge, Spinner, HStack, Tooltip, useToast } from "@chakra-ui/react"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"

/**
 * Orders -> Import log (masters): every orders file that came in — hourly EDI pickup or
 * Imports -> PNP -> Orders — scoped to the organisation toggle. Two maintenance buttons:
 *  - Re-resolve: after a vendor code is mapped to a supplier (Imports -> New Data ->
 *    Unmatched) or new article keys were learned, re-run the resolution on the lines
 *    that still have no supplier or no PnP article number.
 *  - Learn article keys: read the archived PnP sales files for barcode / product code ->
 *    PnP article number (bounded per click; scripts/orders-setup.js does the full backlog).
 */
const API = process.env.REACT_APP_API_URL

const ImportLog = () => {
  const { selectedOrg, isMaster } = useSupplierRetailer()
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState("")
  const toast = useToast()
  const navigate = useNavigate()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.post(`${API}/orders/files`, { org_code: isMaster ? selectedOrg || null : null, limit: 200 })
      setFiles(res.data.data || [])
    } catch (err) {
      toast({ title: "Could not load the import log", description: err.response?.data?.message || err.message, status: "error", duration: 4000 })
    } finally {
      setLoading(false)
    }
  }, [selectedOrg, isMaster, toast])

  useEffect(() => { load() }, [load])

  const run = async (path, label) => {
    setBusy(path)
    try {
      const res = await axios.post(`${API}/orders/${path}`, {})
      toast({ title: label, description: res.data.message, status: "success", duration: 6000, isClosable: true })
      load()
    } catch (err) {
      toast({ title: `${label} failed`, description: err.response?.data?.message || err.message, status: "error", duration: 5000 })
    } finally {
      setBusy("")
    }
  }

  return (
    <Box>
      <Flex mb={3} align="center" gap={3} wrap="wrap">
        <Text fontSize="sm" color="gray.500">
          Orders files received for {selectedOrg || "all organisations"} — from the hourly PnP EDI pickup and from Imports → PNP → Orders.
        </Text>
        <HStack ml="auto">
          <Tooltip label="Re-run supplier + PnP article matching on lines that still have no supplier or no article number (after mapping a vendor code or learning new keys)">
            <Button size="sm" variant="outline" onClick={() => run("re-resolve", "Re-resolve")} isLoading={busy === "re-resolve"} isDisabled={!!busy}>Re-resolve unresolved lines</Button>
          </Tooltip>
          <Tooltip label="Read the archived PnP sales files for barcode / product code → PnP article number">
            <Button size="sm" variant="outline" onClick={() => run("refresh-keys", "Learn article keys")} isLoading={busy === "refresh-keys"} isDisabled={!!busy}>Learn article keys</Button>
          </Tooltip>
          <Button size="sm" onClick={load} isDisabled={loading}>Reload</Button>
        </HStack>
      </Flex>
      {loading ? (
        <Flex justify="center" py={8}><Spinner /></Flex>
      ) : files.length === 0 ? (
        <Text color="gray.500" fontSize="sm">No orders files yet.</Text>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="striped">
            <Thead>
              <Tr>
                <Th>Received</Th>
                <Th>Source</Th>
                <Th>File</Th>
                <Th>Supplier(s)</Th>
                <Th>Org</Th>
                <Th>Order dates</Th>
                <Th isNumeric>POs</Th>
                <Th isNumeric>Stores</Th>
                <Th isNumeric>Lines</Th>
                <Th isNumeric>New</Th>
                <Th isNumeric>Updated</Th>
                <Th isNumeric>Unmatched</Th>
                <Th>Status</Th>
                <Th>By</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {files.map((f) => (
                <Tr key={f.id}>
                  <Td fontSize="xs" whiteSpace="nowrap">{f.created_at}</Td>
                  <Td><Badge colorScheme={f.source === "edi" ? "blue" : "teal"}>{f.source}</Badge></Td>
                  <Td fontSize="xs" maxW="320px" isTruncated title={f.filename}>{f.filename}</Td>
                  <Td fontSize="xs">{f.suppliers || <Text color="orange.500">none</Text>}</Td>
                  <Td fontSize="xs">{f.org_code || "-"}</Td>
                  <Td fontSize="xs" whiteSpace="nowrap">{f.order_date_from}{f.order_date_to && f.order_date_to !== f.order_date_from ? ` → ${f.order_date_to}` : ""}</Td>
                  <Td isNumeric fontSize="xs">{f.po_count}</Td>
                  <Td isNumeric fontSize="xs">{f.store_count}</Td>
                  <Td isNumeric fontSize="xs">{f.lines_total}</Td>
                  <Td isNumeric fontSize="xs">{f.lines_inserted}</Td>
                  <Td isNumeric fontSize="xs">{f.lines_updated}</Td>
                  <Td isNumeric fontSize="xs">{f.lines_unmatched ? <Badge colorScheme="orange">{f.lines_unmatched}</Badge> : 0}</Td>
                  <Td>
                    <Tooltip label={f.message || ""}><Badge colorScheme={f.status === "imported" ? "green" : "red"}>{f.status}</Badge></Tooltip>
                  </Td>
                  <Td fontSize="xs">{f.uploaded_by_name || (f.source === "edi" ? "cron" : f.uploaded_by || "-")}</Td>
                  <Td><Button size="xs" variant="outline" onClick={() => navigate(`/orders?file=${f.id}${f.suppliers ? `&supplier=${encodeURIComponent(String(f.suppliers).split(",")[0].trim())}` : ""}`)} isDisabled={!f.lines_total}>Open lines</Button></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  )
}

export default ImportLog
